<!-- Fichier: frontend/src/components/MeetingCard.vue -->

<template>
  <div class="meeting-card">
    <div class="meeting-card__header" @click="$emit('view', meeting.id)">
      <h3 class="meeting-card__title">{{ meeting.title }}</h3>
      <div class="meeting-card__date">
        <FeatherIcon type="calendar" size="14"></FeatherIcon>
        {{ formattedDate }}
        <span v-if="meeting.meeting_time"> à {{ formattedTime }}</span>
      </div>
    </div>

    <div class="meeting-card__footer">
      <!-- Utilisation des nouveaux champs de compte fournis par l'API -->
      <div class="meeting-card__actions-summary">
        <FeatherIcon type="check-square" size="16"></FeatherIcon>
        {{ meeting.completed_actions || 0 }}
        / {{ meeting.total_actions || 0 }} actions
      </div>
      <!-- Boutons d'édition et suppression -->
      <div class="meeting-card__actions">
        <FeatherIcon
          type="edit-3"
          size="16"
          class="action-icon action-icon--edit"
          @click.stop="$emit('edit', meeting.id)"
        />
        <!-- .stop pour éviter de déclencher le @click principal -->
        <FeatherIcon
          type="trash-2"
          size="16"
          class="action-icon action-icon--delete"
          @click.stop="$emit('delete', meeting.id)"
        />
        <!-- .stop pour éviter de déclencher le @click principal -->
      </div>
    </div>
  </div>
</template>

<script>
import FeatherIcon from "vue-feather"; // Import local si pas enregistré globalement

export default {
  name: "MeetingCard",
  components: {
    FeatherIcon,
  },
  props: {
    meeting: {
      type: Object,
      required: true,
      // Ajouter un validateur simple pour s'assurer que les champs de compte existent
      validator: (value) => {
        return (
          value &&
          typeof value.total_actions !== "undefined" &&
          typeof value.completed_actions !== "undefined"
        );
      },
    },
  },
  computed: {
    formattedDate() {
      if (!this.meeting || !this.meeting.meeting_date) return "";
      const [year, month, day] = this.meeting.meeting_date
        .split("-")
        .map(Number);
      const dateObj = new Date(year, month - 1, day);
      return dateObj.toLocaleDateString("fr-FR", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    formattedTime() {
      if (!this.meeting || !this.meeting.meeting_time) return "";
      const [hours, minutes] = this.meeting.meeting_time.split(":").map(Number);
      const timeObj = new Date(2000, 0, 1, hours, minutes);
      return timeObj.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  // Déclarer les événements émis
  emits: ["view", "edit", "delete"],
};
</script>

<style scoped lang="scss">
@import "@/assets/css/_variables.scss";

.meeting-card {
  // Copie des styles de base de .app-block:
  background-color: $color-surface;
  padding: $space-lg;
  margin-bottom: 0;
  border-radius: $border-radius-lg;
  box-shadow: $shadow-md;

  // La carte entière n'est plus le seul élément cliquable, donc on retire le cursor pointer principal
  // cursor: pointer;
  transition: transform $transition-duration-short $transition-ease-in-out,
    box-shadow $transition-duration-short $transition-ease-in-out;

  &:hover {
    transform: translateY(-5px);
    box-shadow: $shadow-lg;
  }

  // La partie header est maintenant cliquable pour voir le détail
  .meeting-card__header {
    margin-bottom: $space-md;
    padding-bottom: $space-sm;
    border-bottom: 1px solid $color-background;
    cursor: pointer; // Cursor pointer pour la partie cliquable
  }

  .meeting-card__title {
    font-size: $font-size-lg;
    margin-bottom: $space-sm;
    color: $color-text-900;
  }

  .meeting-card__date {
    font-size: $font-size-sm;
    color: $color-text-600;
    display: flex;
    align-items: center;

    .feather-icon {
      margin-right: $space-xs;
      color: $color-text-300;
    }
  }

  .meeting-card__footer {
    display: flex;
    justify-content: space-between; // Espace entre le résumé et les actions
    align-items: center;
    font-size: $font-size-sm;
    color: $color-text-600;
    margin-top: $space-md;

    .meeting-card__actions-summary {
      display: flex;
      align-items: center;
      flex-shrink: 0; // Empêche de rétrécir
      .feather-icon {
        margin-right: $space-xs;
        color: $color-warning;
      }
    }

    // Styles pour les icônes d'action sur la carte
    .meeting-card__actions {
      display: flex;
      align-items: center;
      gap: $space-sm; // Espace entre les icônes d'action
      flex-shrink: 0; // Empêche de rétrécir

      .action-icon {
        cursor: pointer;
        color: $color-text-300; // Couleur par défaut (gris clair)
        transition: color $transition-duration-short ease-in-out,
          transform $transition-duration-short ease-in-out;

        &:hover {
          transform: scale(1.1); // Léger zoom au survol
          color: $color-primary-500; // Couleur plus vive au survol
        }
        &--edit:hover {
          color: $color-accent-500; // Couleur accent pour l'édition
        }
        &--delete:hover {
          color: $color-error; // Couleur erreur pour la suppression
        }
      }
    }
  }
}
</style>
